import React, { Suspense, lazy, useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import Layout from "./components/Layout";
// ETF är ett personligt systemadmin-verktyg (beslut 2026-10-01) — strikt rollvakt.
import SystemadminRoute from './components/SystemadminRoute';
import { ToastProvider } from './components/ui/ToastProvider';
import DialogProvider from './components/ui/dialog/DialogProvider';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import { sparaNuvarandeSomInloggningsmal } from './utils/postLoginRedirect';
import { WSProvider } from './communication/WSProvider';
import { BoardReportProvider } from './contexts/BoardReportContext';
import { ArshjulPanelProvider } from './modules/Arshjul/ArshjulPanelContext';
import { ThemeProvider } from './contexts/ThemeProvider';
import { TenantGate } from './components/TenantGate';
import MaintenanceGate from './components/Maintenance/MaintenanceGate';
import LoginPage from './components/LoginPage';
import StartOmdirigering from './components/login/StartOmdirigering';
import IceView from './components/ICE/IceView';
import ExternalIceView from './components/ICE/ExternalIceView';
import PageLoadingSkeleton from './components/ui/PageLoadingSkeleton';
const ProjectList = lazy(() => import("./components/ProjectList"));
const ProjectDetails = lazy(() => import("./components/ProjectDetails"));
const FragaSvarExternPage = lazy(() => import("./modules/FragaSvarExtern/FragaSvarExternPage")); // extern svarslänk Fråga & Svar (RFI)
const JsxPlayground = lazy(() => import('./components/JsxPlayground'));
const DwgViewerTestPage = lazy(() => import('./components/DwgViewerTestPage'));
const MangdPdfParserPage = lazy(() => import('./components/MangdPdfParserPage'));
const AIModule = lazy(() => import("./components/AIModule"));
const ProjectMapModule = lazy(() => import("./components/ProjectMapModule"));
const AnbudModule = lazy(() => import("./modules/Anbud/AnbudModule"));
const AnbudV2Module = lazy(() => import("./modules/AnbudV2/AnbudV2Module"));
const AnbudDetaljPage = lazy(() => import("./modules/Anbud/pages/AnbudDetaljPage"));
// ADR 0009: publik magic-link-vy for godkann/avsla av projektatkomst
const MagicLinkApprovePage = lazy(() => import("./modules/ProjektKatalog/MagicLinkApprovePage"));
const HistorikarkivModule = lazy(() => import("./modules/Historikarkiv/HistorikarkivModule"));
const SmartdokArkivModule = lazy(() => import("./modules/SmartdokArkiv/SmartdokArkivModule"));
const PortfoljriskModule = lazy(() => import("./modules/Portfoljrisk/PortfoljriskModule"));
const FramdriftPortfoljModule = lazy(() => import("./modules/TidplanPortfolj/FramdriftPortfoljModule"));
const RegelverkModule = lazy(() => import("./modules/Regelverk/RegelverkModule"));
const ArbetsmiljodelegeringModule = lazy(() => import("./modules/Arbetsmiljodelegering/ArbetsmiljodelegeringModule"));
const GrupperModule = lazy(() => import("./modules/Grupper/GrupperModule"));
const LarandeModule = lazy(() => import("./modules/Larande/LarandeModule"));
const InstruktionerModule = lazy(() => import("./modules/Instruktioner/InstruktionerModule"));
// Ledningssystemets delar som egna moduler (placering 2026-10 steg 1 och 3)
const AvvikelserModule = lazy(() => import("./modules/Avvikelser/AvvikelserModule"));
const MalKpiModule = lazy(() => import("./modules/MalKpi/MalKpiModule"));
const LagbevakningSida = lazy(() => import("./modules/Lagbevakning/LagbevakningSida"));
const HomeOverview = lazy(() => import("./components/HomeOverview"));
const ManagementSystemRoot = lazy(() => import("./components/ManagementSystem"));
const BuyerView = lazy(() => import("./components/BuyerView"));
const ProfilePage = lazy(() => import('./components/ProfilePage'));
const AcceptInvitePage = lazy(() => import('./components/AcceptInvitePage'));
const ResetPasswordPage = lazy(() => import('./components/ResetPasswordPage'));
const InboxPage = lazy(() => import('./components/InboxPage'));
const IssuesModule = lazy(() => import('./components/IssuesModule'));
const IssuesOverview = lazy(() => import('./components/IssuesOverview'));
const MeetingModule = lazy(() => import('./components/MeetingModule'));
const QaControlsModule = lazy(() => import('./components/QaControlsModule'));
/* ── Training V2 (redesigned) ── */
const TrainingLandingPage = lazy(() => import('./components/training/TrainingLandingPage'));
const TrainingDashboardV2 = lazy(() => import('./components/training/TrainingDashboardV2'));
const TrainingWelcomeV2 = lazy(() => import('./components/training/TrainingWelcomeV2'));
const TrainingPlayerV2 = lazy(() => import('./components/training/TrainingPlayerV2'));
const TrainingCatalogV2 = lazy(() => import('./components/training/TrainingCatalogV2'));
const TrainingAdminV2 = lazy(() => import('./components/training/TrainingAdminV2'));
const TrainingResults = lazy(() => import('./components/training/TrainingResults'));
const TrainingShell = lazy(() => import('./components/training/TrainingShell'));
/* ── Training V1 (kept for external/knowledge/glossary/schema) ── */
const TrainingExternalView = lazy(() => import('./components/TrainingExternalView'));
const ObservationSignering = lazy(() => import('./components/training/ObservationSignering'));
const TrainingSchema = lazy(() => import('./components/TrainingSchema'));
const TrainingGlossary = lazy(() => import('./components/TrainingGlossary'));
const TrainingKnowledgeLibrary = lazy(() => import('./components/TrainingKnowledgeLibrary'));
const TrainingSearch = lazy(() => import('./components/TrainingSearch'));
const RevisionsPage = lazy(() => import('./components/RevisionsPage'));
const RevisionDetailPage = lazy(() => import('./components/RevisionDetailPage'));
const ResourcePlanner = lazy(() => import('./components/ResourcePlanner'));
const ContactList = lazy(() => import('./components/ContactList'));
const PurchasingModule = lazy(() => import('./components/PurchasingModule'));
const OrphanInvoicesPage = lazy(() => import('./components/OrphanInvoicesPage'));
const InsightList = lazy(() => import('./components/InsightList'));
const InsightDetail = lazy(() => import('./components/InsightDetail'));
const ContactOrgPlanV2 = lazy(() => import('./components/ContactOrgPlanV2'));
const HogiaTestPage = lazy(() => import('./components/HogiaTestPage'));
const HogiaReadModule = lazy(() => import('./components/HogiaReadModule'));
const KanCalcModule = lazy(() => import('./components/KanCalcModule'));
const KanplanMVP = lazy(() => import('./components/KanplanMVP'));
const KanaiModule = lazy(() => import('./components/KanaiModule'));
const SystemAdminModule = lazy(() => import('./modules/SystemAdmin/SystemAdminModule'));
const PublicInskrivningPage = lazy(() => import('./modules/InskrivningPublic/PublicInskrivningPage'));
const MinaDokumentPage = lazy(() => import('./modules/InskrivningPublic/pages/MinaDokumentPage'));
const PartnerViewPage = lazy(() => import('./modules/Inskrivning/pages/PartnerViewPage'));
const InskrivningAdminAccessEntry = lazy(() =>
  import('./modules/Inskrivning/AdminAccessShell').then((m) => ({ default: m.AdminAccessEntry })),
);
const InskrivningAdminAccessShell = lazy(() =>
  import('./modules/Inskrivning/AdminAccessShell').then((m) => ({ default: m.AdminAccessShell })),
);
const ManagementDashboardPage = lazy(() => import('./modules/ManagementDashboard/ManagementDashboardPage'));
const ManagementDashboardPageV2 = lazy(() => import('./modules/ManagementDashboard/ManagementDashboardPage_v2'));
const FinanceDashboardPage = lazy(() => import('./modules/FinanceDashboard/FinanceDashboardPage'));
const TenantAdminModule = lazy(() => import('./modules/TenantAdmin/TenantAdminModule'));
const FieldFeaturesPanel = lazy(() => import('./modules/TenantAdmin/FieldFeaturesPanel'));
const ExpenseSettingsPanel = lazy(() => import('./modules/TenantAdmin/ExpenseSettingsPanel'));
// Avtalsregler för Inköp (Fas 4). Egen rutt i stället för en flik i
// TenantAdminModule.jsx — den filen är 11 000 rader och får enligt
// CLAUDE.md inte växa mer utan uppdelning.
const AvtalsregelPanel = lazy(() => import('./modules/TenantAdmin/avtalsregler/AvtalsregelPanel'));
const DocumentPurgePanel = lazy(() => import('./modules/TenantAdmin/DocumentPurgePanel'));
const ContractsModule = lazy(() => import('./modules/Contracts/ContractsModule'));
const BimViewerModule = lazy(() => import('./modules/BimViewer/BimViewerModule'));
const SuppliersModule = lazy(() => import('./modules/Suppliers/SuppliersModule'));
const CrmModule = lazy(() => import('./modules/Crm/CrmModule'));
const EquipPassOwnerPage = lazy(() => import('./modules/MaskinQR/EquipPassOwnerPage'));
const EquipPassPublicPage = lazy(() => import('./modules/MaskinQR/EquipPassPublicPage'));
const EquipPassPublicCodePage = lazy(() => import('./modules/MaskinQR/EquipPassPublicCodePage'));
const KalkylReviewPage = lazy(() => import('./modules/KalkylReview/KalkylReviewPage'));
const EtfAllocationPage = lazy(() => import('./modules/EtfAllocation/EtfAllocationPage'));
const AssessmentModule = lazy(() => import('./modules/Assessments/AssessmentModule'));
const ExternalAssessmentPage = lazy(() => import('./modules/Assessments/ExternalAssessmentPage'));
const LeadershipAssessmentModule = lazy(() => import('./modules/LeadershipAssessment/LeadershipAssessmentModule'));
const ExternalLeadershipForm = lazy(() => import('./modules/LeadershipAssessment/ExternalLeadershipForm'));
const ManagerDashboard = lazy(() => import('./modules/Assessments/ManagerDashboard'));
const ManagerProtokoll = lazy(() => import('./modules/Assessments/SamtalsProtokoll'));
const SupplierSurveyPublicPage = lazy(() => import('./modules/SupplierSurveys/SupplierSurveyPublicPage'));
// Leverantörsportal för offertförfrågan — publik, token i länken (Inköp).
const OffertPortalPage = lazy(() => import('./modules/Inkop/portal/OffertPortalPage'));
// UE-portal for saker schakt - publik, token i lanken. UE:s schaktansvarig
// har inget KanPlan-konto och svarar for sina egna schakter via lanken.
const SchaktrondUePortalPage = lazy(() => import('./modules/Schaktrond/portal/UePortalPage'));
const EkExternPage = lazy(() => import('./modules/EkExtern/EkExternPage'));
// Dela handlingar: UE öppnar delade projekthandlingar via länk utan konto.
const DeladeHandlingarPage = lazy(() => import('./modules/DeladeHandlingar/DeladeHandlingarPage'));
const BestallarVyPage = lazy(() => import('./modules/BestallarVy/BestallarVyPage'));
const EmployeeSurveyPublicPage = lazy(() => import('./modules/HRM/EmployeeSurveyPublicPage'));
const ProvanstallningSvarPage = lazy(() => import('./modules/HRM/ProvanstallningSvarPage'));
const BakAvstamningPage = lazy(() => import('./modules/BakAvstamning/BakAvstamningPage'));
const EkBibliotekPage = lazy(() => import('./modules/EkBibliotek/EkBibliotekPage'));
const StandaloneEkBibliotek = lazy(() => import('./modules/EkBibliotek/StandaloneEkBibliotek'));
const ExternalEkBibliotek = lazy(() => import('./modules/EkBibliotek/ExternalEkBibliotek'));
const StandaloneBakAvstamning = lazy(() => import('./modules/Standalone/StandaloneBakAvstamning'));
const RevisionResponsePage = lazy(() => import('./components/RevisionResponsePage'));
const IssueExternalResponsePage = lazy(() => import('./components/IssueExternalResponsePage'));
// Chefens attest av utlägg via länk i mejlet (utläggspiloten 2026-10-06)
const UtlaggAttestExternPage = lazy(() => import('./modules/Utlagg/extern/UtlaggAttestExternPage'));
const RevisionRespondInternalPage = lazy(() => import('./components/RevisionRespondInternalPage'));
const MinaRevisionsatgarderPage = lazy(() => import('./modules/MinaRevisionsatgarder/MinaRevisionsatgarderPage'));
const OpenBooksPortal = lazy(() => import('./modules/OpenBooks/OpenBooksPortal'));
const OpenBooksAdmin = lazy(() => import('./modules/OpenBooks/OpenBooksAdmin'));
const TimeReportingModule = lazy(() => import('./modules/TimeReporting/TimeReportingModule'));
const TimeReportingPortal = lazy(() => import('./modules/TimeReporting/TimeReportingPortal'));
const UtlaggModule = lazy(() => import('./modules/Utlagg/UtlaggModule'));
const UtlaggAttestLanding = lazy(() => import('./modules/Utlagg/UtlaggAttestLanding'));
// Ledighet (Mikael F1/F17 2026-10-23): modulen och länksidorna för chef och medarbetare.
const LedighetModule = lazy(() => import('./modules/Ledighet/LedighetModule'));
const LedighetBeslutsSida = lazy(() => import('./modules/Ledighet/public/BeslutsSida'));
const LedighetSvarsSida = lazy(() => import('./modules/Ledighet/public/SvarsSida'));
const ArbetsberedningModule = lazy(() => import('./modules/Arbetsberedning/ArbetsberedningModule'));
const BildbankModule = lazy(() => import('./modules/Bildbank/BildbankModule'));
const PayrollModule = lazy(() => import('./modules/Payroll/PayrollModule'));
const HRMModule = lazy(() => import('./modules/HRM/HRMModule'));
const KompetensModule = lazy(() => import('./modules/Kompetens/KompetensModule'));
const KanProfilModule = lazy(() => import('./modules/KanProfil/KanProfilModule'));
const KanProfilTeamView = lazy(() => import('./modules/KanProfil/team/TeamView'));
const StandaloneKanProfil = lazy(() => import('./modules/Standalone/StandaloneKanProfil'));
const KanProfilAdminPage = lazy(() => import('./modules/KanProfil/admin/KanProfilAdminPage'));
const AvtalsbyggareModule = lazy(() => import('./modules/Avtalsbyggare/AvtalsbyggareModule'));
const HMSModule = lazy(() => import('./modules/HMS/HMSModule'));
const StandalonePayroll = lazy(() => import('./modules/Standalone/StandalonePayroll'));
const StandaloneOrgPlan = lazy(() => import('./orgplan/StandaloneOrgPlan'));
const ExternalOrgPlanView = lazy(() => import('./orgplan/ExternalOrgPlanView'));
const StandaloneOpenBooks = lazy(() => import('./modules/Standalone/StandaloneOpenBooks'));
const StandaloneManagementSystem = lazy(() => import('./modules/Standalone/StandaloneManagementSystem'));
const EmbedManagementSystem = lazy(() => import('./modules/Standalone/EmbedManagementSystem'));
const StandaloneTraining = lazy(() => import('./modules/Standalone/StandaloneTraining'));
const EmbedTraining = lazy(() => import('./modules/Standalone/EmbedTraining'));
const StandaloneSkolaPortal = lazy(() => import('./modules/Skola/StandaloneSkolaPortal'));
const ProjectOrgPlanPage = lazy(() => import('./orgplan/ProjectOrgPlanPage'));
const StandaloneMangdavstamning = lazy(() => import('./modules/Standalone/StandaloneMangdavstamning'));
const ExternalMangdView = lazy(() => import('./components/ExternalMangdView'));
const ExternalBakView = lazy(() => import('./components/ExternalBakView'));
const VillkorPage = lazy(() => import('./components/VillkorPage'));
const ChangelogPage = lazy(() => import('./components/ChangelogPage'));
const RoadmapPage = lazy(() => import('./components/roadmap/RoadmapPage'));
const PaGangPage = lazy(() => import('./modules/PaGang/PaGangPage'));
const PaGangUndersida = lazy(() => import('./modules/PaGang/pages/PaGangUndersida'));
const FleetModule = lazy(() => import('./modules/Fleet/FleetModule'));
const VaxelModule = lazy(() => import('./modules/Vaxel/Module'));
const StandaloneFleet = lazy(() => import('./modules/Standalone/StandaloneFleet'));
const ExternalFleetView = lazy(() => import('./modules/Fleet/ExternalFleetView'));
const FleetCfoAnswer = lazy(() => import('./modules/Fleet/orders_v2/public/FleetCfoAnswer'));
const FleetExternalApproval = lazy(() => import('./modules/Fleet/approval/FleetExternalApproval'));
const InfobricFleetDevModule = lazy(() => import('./modules/InfobricFleetDev/InfobricFleetDevModule'));
const KlimatModule = lazy(() => import('./modules/Klimat/KlimatModule'));
const StandaloneKlimat = lazy(() => import('./modules/Standalone/StandaloneKlimat'));
const StandaloneRevisions = lazy(() => import('./modules/Standalone/StandaloneRevisions'));
const ExternalKlimatView = lazy(() => import('./modules/Klimat/ExternalKlimatView'));
const KlimatDemoPage = lazy(() => import('./modules/Klimat/KlimatDemoPage'));
const CvGeneratorModule = lazy(() => import('./modules/CvGenerator/CvGeneratorModule'));
const StandaloneCvGenerator = lazy(() => import('./modules/Standalone/StandaloneCvGenerator'));
const ExternalCvView = lazy(() => import('./modules/CvGenerator/pages/ExternalCvView'));
const AccModule = lazy(() => import('./modules/ACC/AccModule'));
const EftermarknadModule = lazy(() => import('./modules/Eftermarknad/EftermarknadModule'));
const ExternalPortalView = lazy(() => import('./modules/Eftermarknad/ExternalPortalView'));
const DaluxModule = lazy(() => import('./modules/Dalux/DaluxModule'));
const FeedbackBoard = lazy(() => import('./modules/Feedback/FeedbackBoard'));
const ReportPage = lazy(() => import('./modules/Feedback/ReportPage'));
const FieldApp = lazy(() => import('./field/FieldApp'));
const FieldLoginScreen = lazy(() => import('./field/screens/FieldLoginScreen'));
const FieldIntro = lazy(() => import('./field/screens/FieldIntro'));
const FieldAccessGate = lazy(() => import('./field/FieldAccessGate'));
/* Externa resurser P1: eget skal for inhyrda, egen inloggning (cookie kanplan_extern), egen grind. Ingen AuthProvider. */
const ExternApp = lazy(() => import('./field/extern/ExternApp'));

/* Self-contained Field entry: shows login if unauthenticated, FieldApp if authenticated.
   Never redirects to /login — the user stays on /field at all times. */
function FieldSplash() {
  /* Visar KanPlan-logo + spinner direkt sa anvandaren slipper se blank skarm
     under auth-loading eller lazy-chunk-fetch. Statisk ingen state — snabbt.
     Animation 0.6s (var 0.9s) for snappare upplevelse. */
  return (
    <div style={{ position: 'fixed', inset: 0, background: '#1A2332', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16, zIndex: 9999, fontFamily: "Inter, -apple-system, 'SF Pro Display', 'Helvetica Neue', sans-serif" }}>
      <div style={{ width: 48, height: 48, borderRadius: 12, background: '#3B82F6', display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'fldfadein 0.25s ease-out' }}>
        <span style={{ fontSize: 22, fontWeight: 800, color: '#fff' }}>K</span>
      </div>
      <div style={{ width: 32, height: 32, border: '3px solid rgba(255,255,255,0.18)', borderTopColor: '#3B82F6', borderRadius: '50%', animation: 'fldspin 0.6s linear infinite' }} />
      <style>{'@keyframes fldspin { to { transform: rotate(360deg); } } @keyframes fldfadein { from { opacity: 0; transform: scale(0.85); } to { opacity: 1; transform: scale(1); } }'}</style>
    </div>
  );
}

/* Preload av field-chunken sa lazy(import) inte behover blocka pa initial navigering.
   Triggas pa idle-callback (eller direkt pa browsers utan idle-API), gor att
   navigering till /field i de flesta fall blir near-instant. */
if (typeof window !== 'undefined') {
  const preloadField = () => {
    import('./field/FieldApp').catch(() => {});
    import('./field/screens/FieldLoginScreen').catch(() => {});
  };
  if (window.requestIdleCallback) {
    window.requestIdleCallback(preloadField, { timeout: 2000 });
  } else {
    setTimeout(preloadField, 800);
  }
}
function FieldEntry() {
  const { user, loading } = useAuth();
  const [introDone, setIntroDone] = useState(() => {
    try { return localStorage.getItem('field-intro-seen') === '1'; } catch { return false; }
  });

  /* Las dokument-scrollen sa lange /field ar monterad — annars kan sidan
     dras/panas i sidled och studsa (rubber-band), och botten-navigeringen
     kan hamna under synlig vy efter ett drag-slapp. Varje Field-skarm ar
     byggd for att fylla exakt 100dvh internt (se field-tokens.css), sa det
     enda som ska scrolla ar innehallet INUTI skalet — inte dokumentet.
     position:fixed pa body ar det mest robusta sattet att helt neka
     dokument-scroll pa mobil (samma monster som moderna modal-bibliotek
     anvander for scroll-lock). Aterstalls pa unmount sa desktop-sidorna
     inte paverkas nar man lamnar /field. Mikael 2026-09-22: fel rapporterat
     som "gar att dra appen i sidled, botten-menyn hamnar utanfor synlig vy". */
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevScrollY = window.scrollY;
    const prev = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyOverscroll: body.style.overscrollBehavior,
      bodyPosition: body.style.position,
      bodyWidth: body.style.width,
      bodyHeight: body.style.height,
      bodyTop: body.style.top,
    };
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.overscrollBehavior = 'none';
    body.style.position = 'fixed';
    body.style.width = '100%';
    body.style.height = '100%';
    body.style.top = `-${prevScrollY}px`;
    return () => {
      html.style.overflow = prev.htmlOverflow;
      body.style.overflow = prev.bodyOverflow;
      body.style.overscrollBehavior = prev.bodyOverscroll;
      body.style.position = prev.bodyPosition;
      body.style.width = prev.bodyWidth;
      body.style.height = prev.bodyHeight;
      body.style.top = prev.bodyTop;
      window.scrollTo(0, prevScrollY);
    };
  }, []);

  if (loading) return <FieldSplash />;
  if (!user) {
    if (!introDone) {
      return (
        <FieldIntro onDone={() => {
          try { localStorage.setItem('field-intro-seen', '1'); } catch { /* ignore */ }
          setIntroDone(true);
        }} />
      );
    }
    return <FieldLoginScreen />;
  }
  return (
    <TenantGate>
      <WSProvider userId={user?.id}>
        {/* Modulnyckeln "field" avgor vem som far oppna mobilappen alls.
            Skarmarna inuti grindas var for sig som forut. */}
        <FieldAccessGate>
          <FieldApp />
        </FieldAccessGate>
      </WSProvider>
    </TenantGate>
  );
}
const SkyddsronderModule = lazy(() => import('./modules/Skyddsronder/SkyddsronderModule'));
const SkyddsronderUtforPage = lazy(() => import('./modules/Skyddsronder/pages/UtforPage'));
const SkyddsronderSigneraPage = lazy(() => import('./modules/Skyddsronder/pages/SigneraPage'));
const ReferenceProjectsModule = lazy(() => import('./modules/ReferenceProjects/ReferenceProjectsModule'));
// Agent HQ avstangd 2026-08-12 (Mikaels beslut). Rutten och lazy-importen
// borttagna; backend-ytan grindas pa nyckeln legacy_avstangd. Filerna under
// modules/AgentHQ/ ligger kvar for borttagning i eget svep.
const ModuleLibraryPage = lazy(() => import('./modules/ModuleLibrary/ModuleLibraryPage'));
const SignatureGeneratorModule = lazy(() => import('./modules/SignatureGenerator/SignatureGeneratorModule'));
const ExternalSignatureGeneratorView = lazy(() => import('./modules/SignatureGenerator/ExternalSignatureGeneratorView'));
const ModuleAnalyticsModule = lazy(() => import('./modules/ModuleAnalytics/ModuleAnalyticsModule'));
const ProductPresentationPage = lazy(() => import('./modules/ProductPresentation/ProductPresentationPage'));
const InsightAnalyticsPage = lazy(() => import('./modules/InsightAnalytics/InsightAnalyticsPage'));
const WasteAnalysisPage = lazy(() => import('./modules/WasteAnalysis/WasteAnalysisPage'));
const EnergyPage = lazy(() => import('./modules/Energy/EnergyPage'));
const RoutineManagementModule = lazy(() => import('./modules/RoutineManagement/RoutineManagementModule'));
const StandaloneRoutineManagement = lazy(() => import('./modules/Standalone/StandaloneRoutineManagement'));
const RedigeringsvyPage = lazy(() => import('./modules/Redigeringsvy/RedigeringsvyPage')); // F53 KanAI i Excel
const ExternalRoutineReview = lazy(() => import('./modules/RoutineManagement/ExternalRoutineReview'));
const UEListaModule = lazy(() => import('./modules/UELista/UEListaModule'));
const FormsModule = lazy(() => import('./modules/Forms/FormsModule'));
const ExecutiveDashboardPage = lazy(() => import('./modules/ExecutiveDashboard/ExecutiveDashboardPage'));
const ProjectDashboardPage = lazy(() => import('./modules/ProjectDashboard/ProjectDashboardPage'));
const TidplanPage = lazy(() => import('./modules/Tidplan/TidplanPage'));
const InskrivningModule = lazy(() => import('./modules/Inskrivning/InskrivningModule'));
const ForetagsinskrivningModule = lazy(() => import('./modules/Foretagsinskrivning/ForetagsinskrivningModule'));
const PublicForetagsinskrivningPage = lazy(() => import('./modules/ForetagsinskrivningPublic/PublicForetagsinskrivningPage'));
const InventarierModule = lazy(() => import('./modules/Inventarier/InventarierModule'));
const Reports360Page = lazy(() => import('./modules/Reports360/Reports360Page'));
const SpatialPage = lazy(() => import('./modules/Spatial/SpatialPage'));
const VraInboxPage = lazy(() => import('./modules/Spatial/vra/VraInboxPage'));

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) {
    // Kom ihag djuplanken aven nar sessionen faller bort utan ett 401-svar
    // (403, timeout, utloggning) — da hinner fetch-avlyssnaren i index.js inte
    // spara nagot, och anvandaren skulle annars tappa sidan de var pa vag till.
    sparaNuvarandeSomInloggningsmal();
    return <Navigate to="/login" replace />;
  }
  // Single app-level WS connection for all protected content
  return (
    <TenantGate>
      <WSProvider userId={user?.id}>
        <BoardReportProvider>
          <ArshjulPanelProvider>{children}</ArshjulPanelProvider>
        </BoardReportProvider>
      </WSProvider>
    </TenantGate>
  );
}

function FieldProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <FieldLoginScreen />;
  return (
    <TenantGate>
      <WSProvider userId={user?.id}>
        {children}
      </WSProvider>
    </TenantGate>
  );
}

/* Redirect helper: anything under /field that isn't handled goes to /field */
function FieldRedirect() {
  return <Navigate to="/field" replace />;
}

function ExternalManagementSystemRoute() {
  const { token } = useParams();
  return <ManagementSystemRoot externalToken={token} />;
}

function ExternalEtfAllocationRoute() {
  const { token } = useParams();
  return <EtfAllocationPage externalToken={token} />;
}

function App() {
  return (
    <MaintenanceGate>
    <Router>
      <ToastProvider>
        <DialogProvider>
        <ThemeProvider>
        <Routes>
          <Route path="/equippass/code" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <EquipPassPublicCodePage />
            </Suspense>
          } />
          <Route path="/equippass/:token" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <EquipPassPublicPage />
            </Suspense>
          } />
          <Route path="/supplier-surveys/:token" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <SupplierSurveyPublicPage />
            </Suspense>
          } />
          <Route path="/offert/:token" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <OffertPortalPage />
            </Suspense>
          } />
          <Route path="/schaktrond-ue/:token" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <SchaktrondUePortalPage />
            </Suspense>
          } />
          <Route path="/ek-extern/:token" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <EkExternPage />
            </Suspense>
          } />
          <Route path="/delat/:token" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <DeladeHandlingarPage />
            </Suspense>
          } />
          <Route path="/kontrollplan-extern/:token" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <BestallarVyPage />
            </Suspense>
          } />
          <Route path="/survey/:token" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <EmployeeSurveyPublicPage />
            </Suspense>
          } />
          <Route path="/provanstallning/svar" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <ProvanstallningSvarPage />
            </Suspense>
          } />
          <Route path="/assessments/ext/:token" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <ExternalAssessmentPage />
            </Suspense>
          } />
          <Route path="/leadership-assessment/ext/:token" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <ExternalLeadershipForm />
            </Suspense>
          } />
          <Route path="/assessments/manager/:token" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <ManagerDashboard />
            </Suspense>
          } />
          <Route path="/assessments/manager/:managerToken/protocol/:sessionId" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <ManagerProtokoll />
            </Suspense>
          } />
          <Route path="/inskrivning/partner/:token" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <PartnerViewPage />
            </Suspense>
          } />
          <Route path="/inskrivning/admin-access/:projectId" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <InskrivningAdminAccessEntry />
            </Suspense>
          } />
          <Route path="/inskrivning/admin-shell/:projectId/*" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <InskrivningAdminAccessShell />
            </Suspense>
          } />
          {/* Den inskrivnes egen dokumentvy — lankas fran godkannandemejlet.
              Maste ligga FORE /inskrivning/:code sa "mina-dokument" inte
              tolkas som en inskrivningskod. */}
          <Route path="/inskrivning/mina-dokument/:inskrivningId" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <MinaDokumentPage />
            </Suspense>
          } />
          <Route path="/inskrivning/:code" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <PublicInskrivningPage />
            </Suspense>
          } />
          {/* Foretagsinskrivning publika UE-flode (no-auth via kod) */}
          <Route path="/foretagsinskrivning/ue/:code" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <PublicForetagsinskrivningPage />
            </Suspense>
          } />
          <Route path="/ue/:code" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <PublicForetagsinskrivningPage />
            </Suspense>
          } />
          {/* ADR 0009: publik magic-link-vy for godkann/avsla av projektatkomst */}
          <Route path="/projekt-atkomst/godkann" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <MagicLinkApprovePage />
            </Suspense>
          } />
          <Route path="/management-dashboard/external/:token" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <ManagementDashboardPage />
            </Suspense>
          } />
          <Route path="/management-system/external/:token/*" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <ExternalManagementSystemRoute />
            </Suspense>
          } />
          <Route path="/management-system/standalone/*" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <StandaloneManagementSystem />
            </Suspense>
          } />
          <Route path="/management-system/embed/:token/*" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <EmbedManagementSystem />
            </Suspense>
          } />
          <Route path="/ice/external/:token" element={<ExternalIceView />} />
          <Route path="/etf-allocation/external/:token" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <ExternalEtfAllocationRoute />
            </Suspense>
          } />
          <Route path="/signature-generator/external/:token" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <ExternalSignatureGeneratorView />
            </Suspense>
          } />
          <Route path="/open-books/external/:token" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <OpenBooksPortal />
            </Suspense>
          } />
          <Route path="/time-reporting/external/:token" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <TimeReportingPortal />
            </Suspense>
          } />
          <Route path="/payroll/standalone" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <StandalonePayroll />
            </Suspense>
          } />
          <Route path="/orgplan/standalone" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <StandaloneOrgPlan />
            </Suspense>
          } />
          <Route path="/orgplan/external/:token" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <ExternalOrgPlanView />
            </Suspense>
          } />
          <Route path="/open-books/standalone" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <StandaloneOpenBooks />
            </Suspense>
          } />
          <Route path="/ek-bibliotek/standalone" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <StandaloneEkBibliotek />
            </Suspense>
          } />
          <Route path="/ek-bibliotek/external/:token" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <ExternalEkBibliotek />
            </Suspense>
          } />
          <Route path="/bak-avstamning/standalone" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <StandaloneBakAvstamning />
            </Suspense>
          } />
          <Route path="/bak-avstamning/external/:token" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <ExternalBakView />
            </Suspense>
          } />
          <Route path="/mangdavstamning/standalone" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <StandaloneMangdavstamning />
            </Suspense>
          } />
          <Route path="/mangdavstamning/external/:token" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <ExternalMangdView />
            </Suspense>
          } />
          <Route path="/fleet/standalone/*" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <StandaloneFleet />
            </Suspense>
          } />
          <Route path="/fleet/cfo/:token" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <FleetCfoAnswer />
            </Suspense>
          } />
          <Route path="/fleet/external/:token" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <ExternalFleetView />
            </Suspense>
          } />
          <Route path="/eftermarknad/external/:token" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <ExternalPortalView />
            </Suspense>
          } />
          <Route path="/approve/:token" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <FleetExternalApproval />
            </Suspense>
          } />
          <Route path="/klimat/standalone/*" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <StandaloneKlimat />
            </Suspense>
          } />
          <Route path="/klimat/external/:token" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <ExternalKlimatView />
            </Suspense>
          } />
          <Route path="/cv-generator/standalone/*" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <StandaloneCvGenerator />
            </Suspense>
          } />
          <Route path="/cv/external/:token" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <ExternalCvView />
            </Suspense>
          } />
          <Route path="/klimat/demo" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <KlimatDemoPage />
            </Suspense>
          } />
          <Route path="/revisions/standalone/*" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <StandaloneRevisions />
            </Suspense>
          } />
          <Route path="/revisions/respond/:token" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <RevisionResponsePage />
            </Suspense>
          } />
          <Route path="/issues/respond/:token" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <IssueExternalResponsePage />
            </Suspense>
          } />
          <Route path="/fraga-svar/respond/:token" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <FragaSvarExternPage />
            </Suspense>
          } />
          <Route path="/utlagg/ext/:token" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <UtlaggAttestExternPage />
            </Suspense>
          } />
          {/* Ledighet: nyckeln står efter # och skickas i ett huvud, aldrig i sökvägen (S4) */}
          <Route path="/ledighet/ext" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <LedighetBeslutsSida />
            </Suspense>
          } />
          <Route path="/ledighet/svar" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <LedighetSvarsSida />
            </Suspense>
          } />
          <Route path="/training/standalone/*" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <StandaloneTraining />
            </Suspense>
          } />
          <Route path="/training/embed/:token/*" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <EmbedTraining />
            </Suspense>
          } />
          <Route path="/training/observation/signera" element={
            <Suspense fallback={<PageLoadingSkeleton />}><ObservationSignering /></Suspense>
          } />
          <Route path="/training/external/:token" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <AuthProvider>
                <TrainingExternalView />
              </AuthProvider>
            </Suspense>
          } />
          <Route path="/training/external-preview" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <AuthProvider>
                <ProtectedRoute>
                  <TrainingExternalView previewMode />
                </ProtectedRoute>
              </AuthProvider>
            </Suspense>
          } />
          {/* F53: redigeringsvyn för Excel med KanAI, inloggad men utan sidomeny (Mikael F57 ja 27/10) */}
          <Route path="/projekt/:projectId/dokument/:documentId/redigera" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <AuthProvider>
                <ProtectedRoute>
                  <RedigeringsvyPage />
                </ProtectedRoute>
              </AuthProvider>
            </Suspense>
          } />
          <Route path="/rutinhantering/standalone" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <StandaloneRoutineManagement />
            </Suspense>
          } />
          <Route path="/routines/review/:token" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <ExternalRoutineReview />
            </Suspense>
          } />
          <Route path="/k/invite/:token" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <StandaloneKanProfil />
            </Suspense>
          } />
          <Route path="/accept-invite" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <AcceptInvitePage />
            </Suspense>
          } />
          <Route path="/reset-password" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <ResetPasswordPage />
            </Suspense>
          } />
          <Route path="/field/extern/*" element={
            <Suspense fallback={<FieldSplash />}>
              <ExternApp />
            </Suspense>
          } />
          <Route path="/field/login" element={<Navigate to="/field" replace />} />
          <Route path="/field/*" element={
            <Suspense fallback={<FieldSplash />}>
              <AuthProvider>
                <FieldEntry />
              </AuthProvider>
            </Suspense>
          } />
          <Route path="/skola/*" element={
            <Suspense fallback={<PageLoadingSkeleton />}>
              <StandaloneSkolaPortal />
            </Suspense>
          } />
          <Route path="/login" element={
            <AuthProvider>
              <LoginPage />
            </AuthProvider>
          } />
          <Route path="/*" element={
            <AuthProvider>
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<PageLoadingSkeleton />}>
                  <Routes>
                    <Route path="/" element={<StartOmdirigering />} />
                    <Route path="/tenders" element={<AnbudModule />} />
                    <Route path="/tenders/dashboard" element={<AnbudModule />} />
                    <Route path="/tenders/:tenderId" element={<AnbudDetaljPage />} />
                    <Route path="/anbud-v2/*" element={<AnbudV2Module />} />
                    <Route path="/historikarkiv" element={<HistorikarkivModule />} />
                    <Route path="/smartdok-arkiv" element={<SmartdokArkivModule />} />
                    <Route path="/portfoljrisk" element={<PortfoljriskModule />} />
                    <Route path="/tidplan/framdrift" element={<FramdriftPortfoljModule />} />
                    <Route path="/regelverk/*" element={<RegelverkModule />} />
                    <Route path="/arbetsmiljodelegering" element={<ArbetsmiljodelegeringModule />} />
                    <Route path="/grupper" element={<GrupperModule />} />
                    <Route path="/grupper/:id" element={<GrupperModule />} />
                    <Route path="/larande" element={<LarandeModule />} />
                    <Route path="/larande/:id" element={<LarandeModule />} />
                    <Route path="/instruktioner/*" element={<InstruktionerModule />} />
                    <Route path="/avvikelser/*" element={<AvvikelserModule />} />
                    <Route path="/mal-kpi" element={<MalKpiModule />} />
                    <Route path="/lagbevakning" element={<LagbevakningSida />} />
                    {/* Gamla adresser i Ledningssystemet (placering 2026-10). Mer specifika än catch-all /management-system/* och vinner därför. */}
                    <Route path="/management-system/deviations/*" element={<Navigate to="/avvikelser" replace />} />
                    <Route path="/management-system/improvement-suggestions" element={<Navigate to="/avvikelser/forslag" replace />} />
                    <Route path="/management-system/goals" element={<Navigate to="/mal-kpi" replace />} />
                    <Route path="/management-system/legal" element={<Navigate to="/lagbevakning" replace />} />
                    <Route path="/issues" element={<IssuesModule />} />
                    <Route path="/issues/overview" element={<IssuesOverview />} />
                    <Route path="/meetings" element={<MeetingModule />} />
                    <Route path="/qa/controls" element={<QaControlsModule />} />
                    <Route path="/dev/jsx-playground" element={<JsxPlayground />} />
                    <Route path="/dev/dwg-viewer" element={<DwgViewerTestPage />} />
                    <Route path="/tools/mangd-parser" element={<MangdPdfParserPage />} />
                    <Route path="/home/overview" element={<HomeOverview />} />
                    <Route path="/projects" element={<ProjectList search="" />} />
                    <Route path="/projects/:id" element={<ProjectDetails />} />
                    <Route path="/projects/:id/buyer-view" element={<BuyerView />} />
                    <Route path="/ai" element={<AIModule />} />
                    <Route path="/kanai" element={<KanaiModule />} />
                    <Route path="/admin/*" element={<SystemAdminModule />} />
                    <Route path="/ice" element={<IceView />} />
                    <Route path="/tenant-admin" element={<TenantAdminModule />} />
                    <Route path="/tenant-admin/field-features" element={<FieldFeaturesPanel />} />
                    <Route path="/tenant-admin/expense-settings" element={<ExpenseSettingsPanel />} />
                    <Route path="/tenant-admin/avtalsregler" element={<AvtalsregelPanel />} />
                    {/* Papperskorg for dokument: insyn fore nattjobbets gallring (2026-09-11) */}
                    <Route path="/tenant-admin/dokumentgallring" element={<DocumentPurgePanel />} />
                    <Route path="/tenant-admin/kanprofil" element={<KanProfilAdminPage />} />
                    <Route path="/equippass" element={<EquipPassOwnerPage />} />
                    <Route path="/project-map" element={<ProjectMapModule />} />
                    <Route path="/management-dashboard" element={<ManagementDashboardPage />} />
                    <Route path="/management-dashboard-v2" element={<ManagementDashboardPageV2 />} />
                    <Route path="/finance-dashboard" element={<FinanceDashboardPage />} />
                    <Route path="/management-system/*" element={<ManagementSystemRoot />} />
                    <Route path="/training" element={<TrainingLandingPage />} />
                    <Route path="/training/onboarding" element={<TrainingWelcomeV2 />} />
                    <Route path="/training/welcome" element={<TrainingWelcomeV2 />} />
                    <Route element={<TrainingShell />}>
                      <Route path="/training/dashboard" element={<TrainingDashboardV2 />} />
                      <Route path="/training/catalog" element={<TrainingCatalogV2 />} />
                      <Route path="/training/results" element={<TrainingResults />} />
                    </Route>
                    <Route path="/training/modules/:moduleId" element={<TrainingPlayerV2 />} />
                    <Route path="/training/admin" element={<TrainingAdminV2 />} />
                    <Route path="/training/search" element={<TrainingSearch />} />
                    <Route path="/training/schema" element={<TrainingSchema />} />
                    <Route path="/training/begrepp" element={<TrainingGlossary />} />
                    <Route path="/training/knowledge" element={<TrainingKnowledgeLibrary mode="training" />} />
                    <Route path="/knowledge" element={<TrainingKnowledgeLibrary mode="general" />} />
                    <Route path="/tenant-admin/knowledge" element={<TrainingKnowledgeLibrary mode="tenant-admin" />} />
                    <Route path="/revisions" element={<RevisionsPage />} />
                    <Route path="/revisions/:id" element={<RevisionDetailPage />} />
                    <Route path="/revisions/:id/respond" element={<RevisionRespondInternalPage />} />
                    {/* Egna revisionsåtgärder utan modulen revisions (F24): ingen modulnyckel i Layout */}
                    <Route path="/mina-atgarder/revision" element={<MinaRevisionsatgarderPage />} />
                    <Route path="/resource-planning" element={<ResourcePlanner />} />
                    <Route path="/purchasing" element={<PurchasingModule />} />
                    <Route path="/fakturagranskning" element={<OrphanInvoicesPage />} />
                    <Route path="/suppliers/*" element={<SuppliersModule />} />
                    <Route path="/crm/*" element={<CrmModule />} />
                    <Route path="/contracts/*" element={<ContractsModule />} />
                    <Route path="/bim/*" element={<BimViewerModule />} />
                    <Route path="/contacts" element={<ContactList search="" />} />
                    {/* Gamla visuella organisationsplanen borttagen 2026-10-05 (Mikaels beslut) — adressen leder till den nya. */}
                    <Route path="/contacts/org" element={<Navigate to="/contacts/orgv2" replace />} />
                    <Route path="/contacts/orgv2" element={<ContactOrgPlanV2 />} />
                    <Route path="/orgplan" element={<ProjectOrgPlanPage />} />
                    <Route path="/assessments/*" element={<AssessmentModule />} />
                    <Route path="/leadership-assessment/*" element={<LeadershipAssessmentModule />} />
                    <Route path="/insight" element={<InsightList />} />
                    <Route path="/insight/:id" element={<InsightDetail />} />
                    <Route path="/hogia" element={<HogiaTestPage />} />
                    <Route path="/hogia/read" element={<HogiaReadModule />} />
                    <Route path="/open-books" element={<OpenBooksAdmin />} />
                    <Route path="/time-reporting" element={<TimeReportingModule />} />
                    <Route path="/utlagg" element={<UtlaggModule />} />
                    {/* Landning for attestlanken i utlaggsmejlet — valjer vy efter enhet */}
                    <Route path="/utlagg/attest" element={<UtlaggAttestLanding />} />
                    <Route path="/ledighet/*" element={<LedighetModule />} />
                    <Route path="/bildbank" element={<BildbankModule />} />
                    <Route path="/payroll" element={<PayrollModule />} />
                    <Route path="/hrm" element={<HRMModule />} />
                    <Route path="/hr/utbildning" element={<KompetensModule />} />
                    <Route path="/kanprofil" element={<KanProfilModule />} />
                    <Route path="/kanprofil/laganalys" element={<KanProfilTeamView />} />
                    <Route path="/avtal-byggaren/*" element={<AvtalsbyggareModule />} />
                    <Route path="/hms/*" element={<HMSModule />} />
                    <Route path="/ia/*" element={<HMSModule />} />
                    <Route path="/skyddsronder" element={<SkyddsronderModule />} />
                    <Route path="/skyddsronder/:roundId/utfor" element={<SkyddsronderUtforPage />} />
                    <Route path="/skyddsronder/:roundId/signera" element={<SkyddsronderSigneraPage />} />
                    {/* F53 (2026-10-12): gamla MapCalc-vyn pensionerad — gamla länkar går till KanCalc */}
                    <Route path="/mapcalc" element={<Navigate to="/kancalc" replace />} />
                    <Route path="/kancalc" element={<KanCalcModule />} />
                    <Route path="/etf-allocation" element={<SystemadminRoute><EtfAllocationPage /></SystemadminRoute>} />
                    <Route path="/calcreview" element={<KalkylReviewPage />} />
                    <Route path="/kalkylgenomgang" element={<Navigate to="/calcreview" replace />} />
                    <Route path="/kanplan-mvp" element={<KanplanMVP />} />
                    <Route path="/inbox" element={<InboxPage />} />
                    <Route path="/me" element={<ProfilePage />} />
                    <Route path="/villkor" element={<VillkorPage />} />
                    <Route path="/changelog" element={<ChangelogPage />} />
                    <Route path="/roadmap" element={<RoadmapPage />} />
                    <Route path="/pa-gang" element={<PaGangPage />} />
                    <Route path="/pa-gang/:sida" element={<PaGangUndersida />} />
                    <Route path="/bak-avstamning" element={<BakAvstamningPage />} />
                    <Route path="/ek-bibliotek" element={<EkBibliotekPage />} />
                    <Route path="/arbetsberedningar" element={<ArbetsberedningModule />} />
                    <Route path="/fleet/*" element={<FleetModule />} />
                    <Route path="/infobric-fleet/*" element={<InfobricFleetDevModule />} />
                    <Route path="/vaxel/*" element={<VaxelModule />} />
                    <Route path="/klimat/*" element={<KlimatModule />} />
                    <Route path="/acc/*" element={<AccModule />} />
                    <Route path="/eftermarknad/*" element={<EftermarknadModule />} />
                    <Route path="/dalux/*" element={<DaluxModule />} />
                    <Route path="/cv-generator" element={<CvGeneratorModule />} />
                    <Route path="/reference-projects" element={<ReferenceProjectsModule />} />
                    <Route path="/modules" element={<ModuleLibraryPage />} />
                    <Route path="/signature-generator" element={<SignatureGeneratorModule />} />
                    <Route path="/module-analytics" element={<ModuleAnalyticsModule />} />
                    <Route path="/product-presentation" element={<ProductPresentationPage />} />
                    <Route path="/insight-analytics" element={<InsightAnalyticsPage />} />
                    <Route path="/waste-analysis" element={<WasteAnalysisPage />} />
                    <Route path="/energy" element={<EnergyPage />} />
                    <Route path="/rutinhantering" element={<RoutineManagementModule />} />
                    <Route path="/ue-lista" element={<UEListaModule />} />
                    <Route path="/forms" element={<FormsModule />} />
                    <Route path="/executive-dashboard" element={<ExecutiveDashboardPage />} />
                    <Route path="/projects/:projectId/dashboard" element={<ProjectDashboardPage />} />
                    <Route path="/projects/:projectId/tidplan" element={<TidplanPage />} />
                    <Route path="/projects/:projectId/inskrivning/*" element={<InskrivningModule />} />
                    <Route path="/foretagsinskrivning/*" element={<ForetagsinskrivningModule />} />
                    <Route path="/inventarier/*" element={<InventarierModule />} />
                    <Route path="/reports-360" element={<Reports360Page />} />
                    <Route path="/spatial" element={<SpatialPage />} />
                    <Route path="/spatial/vra" element={<VraInboxPage />} />
                    <Route path="/feedback-board" element={<FeedbackBoard />} />
                    <Route path="/rapportera" element={<ReportPage />} />
                    {/* /admin/* hanteras av SystemAdminModule (systemadmin-skalet, vyer i systemAdminTabs.jsx) */}
                  </Routes>
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            </AuthProvider>
          } />
        </Routes>
        </ThemeProvider>
        </DialogProvider>
      </ToastProvider>
    </Router>
    </MaintenanceGate>
  );
}

export default App;
